import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Award, TrendingUp } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: Users, value: '50+', label: 'Happy Clients' },
    { icon: Target, value: '100+', label: 'Projects Completed' },
    { icon: Award, value: '15+', label: 'Years Experience' },
    { icon: TrendingUp, value: '99%', label: 'Client Satisfaction' },
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1760611656615-db3fad24a314?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Modern Tech Office Team"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
            </div>
            {/* Floating Card */}
            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Award className="text-blue-600" size={24} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">15+</div>
                  <div className="text-sm text-slate-600">Years in Business</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block mb-4">
              <span className="text-sm font-medium tracking-wide uppercase text-blue-600 bg-blue-50 px-4 py-2 rounded-full">
                About Us
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Innovating Software Solutions Since 2010
            </h2>
            
            <p className="text-base leading-relaxed text-slate-600 mb-6">
              IK Software Solution is a leading software development company dedicated to delivering exceptional digital experiences. We combine technical expertise with creative thinking to build solutions that drive business growth.
            </p>
            
            <p className="text-base leading-relaxed text-slate-600 mb-8">
              Our team of experienced developers, designers, and strategists work together to transform complex challenges into elegant solutions. We're passionate about technology and committed to excellence in everything we do.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300"
                  data-testid={`about-stat-${index}`}
                >
                  <stat.icon className="text-blue-600 mb-3" size={28} />
                  <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;